import { type EnvironmentMode, isEnvironmentMode } from './env-mode'

/**
 * Environment published by the core-layer `init-app` plugin at boot.
 *
 * This lives in its own module rather than in `config/index.ts` for two reasons:
 *
 * 1. `config/index.ts` is imported at build time by `libs/config/nuxt.base.config.ts` and
 *    by every `apps/<app>/nuxt.config.ts`. Keeping its export surface unchanged avoids
 *    perturbing the bundler's module graph for a file that sits on every build path.
 * 2. The override is mutable module state with a single responsibility, which does not
 *    belong in a 500-line config module.
 *
 * The plugin cannot hand the value over through Nuxt's own APIs, because `config/index.ts`
 * runs at module-initialization time and in the plain-Node build context, where the `#app`
 * alias does not resolve — so neither `useNuxtApp` nor `tryUseNuxtApp` is available there.
 */
let resolvedEnvironment: EnvironmentMode | undefined

/**
 * Record the environment resolved from Nuxt's runtime config.
 *
 * Called by the core-layer `init-app` plugin. Pass `undefined` to clear it. Values that are
 * not a known `EnvironmentMode` are ignored, so a malformed runtime config cannot push the
 * app into an unknown environment.
 */
export function setResolvedEnvironment(env: string | undefined): void {
  if (env === undefined) {
    resolvedEnvironment = undefined
    return
  }

  if (isEnvironmentMode(env)) {
    resolvedEnvironment = env
  }
}

/** The plugin-supplied environment, or `undefined` before boot has run. */
export function getResolvedEnvironment(): EnvironmentMode | undefined {
  return resolvedEnvironment
}
